import React, { useState, useEffect, useCallback, useMemo } from "react";
import { ShieldCheck, Loader2, CheckCircle2, AlertTriangle, Clock, XCircle, Phone, Calendar } from "lucide-react";
import { toast } from "sonner";
import { fetchWarrantyCompliance, patchAppointment } from "@/lib/dashboardApi";

const META = {
  active: { label: "Active — Compliant", dot: "bg-green-500", chip: "bg-green-50 text-green-700 border-green-200", Icon: CheckCircle2 },
  due_soon: { label: "Maintenance Due", dot: "bg-yellow-400", chip: "bg-yellow-50 text-yellow-700 border-yellow-200", Icon: Clock },
  at_risk: { label: "At Risk", dot: "bg-orange-500", chip: "bg-orange-50 text-orange-700 border-orange-200", Icon: AlertTriangle },
  not_current: { label: "Requirements Not Current", dot: "bg-red-500", chip: "bg-red-50 text-red-700 border-red-200", Icon: XCircle },
};
const ORDER = ["not_current", "at_risk", "due_soon", "active"];

const WarrantyCompliance = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await fetchWarrantyCompliance()); } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { active: 0, due_soon: 0, at_risk: 0, not_current: 0 };
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);
  const filtered = filter === "all" ? rows : rows.filter((r) => r.status === filter);

  const markComplete = async (r) => {
    if (!r.next_appt_id) { toast.error("No scheduled visit to complete"); return; }
    try { await patchAppointment(r.next_appt_id, { status: "Completed" }); toast.success("Visit marked complete"); load(); }
    catch { toast.error("Could not update visit"); }
  };

  return (
    <div className="space-y-5" data-testid="warranty-compliance-page">
      <div className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#F97316]" /><h3 className="text-slate-900 font-semibold text-lg">Lifetime Warranty Compliance</h3></div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {ORDER.map((k) => {
          const m = META[k];
          return (
            <button key={k} onClick={() => setFilter(filter === k ? "all" : k)} data-testid={`wc-count-${k}`}
              className={`text-left rounded-xl border p-4 transition-all ${filter === k ? "ring-2 ring-[#F97316]/40 " : ""}${m.chip}`}>
              <div className="flex items-center gap-2"><span className={`h-2.5 w-2.5 rounded-full ${m.dot}`} /><span className="text-xs font-semibold">{m.label}</span></div>
              <p className="mt-2 text-3xl font-extrabold">{counts[k] || 0}</p>
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <h4 className="font-semibold text-slate-800 text-sm">{filter === "all" ? "All Lifetime Warranty Customers" : META[filter].label} <span className="text-slate-400">({filtered.length})</span></h4>
          {filter !== "all" && <button onClick={() => setFilter("all")} className="text-xs font-semibold text-[#F97316]">Clear filter</button>}
        </div>
        {loading ? (
          <p className="text-slate-400 text-sm py-10 text-center flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-slate-400 text-sm py-10 text-center">No Lifetime Warranty customers{filter !== "all" ? " in this status" : " yet"}. They appear here automatically when a Lifetime proposal is signed.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map((r, i) => {
              const m = META[r.status];
              return (
                <div key={r.customer_id || r.name || i} className="p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`wc-row-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-slate-900">{r.name || "Unnamed"}</p>
                      <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full border ${m.chip}`}><m.Icon className="w-3 h-3" /> {m.label}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{r.system || "—"} · Installed {r.install_date || "—"} · {r.plan}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-1">
                      {r.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {r.phone}</span>}
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Next visit: <b className={r.overdue_count ? "text-red-600" : "text-slate-700"}>{r.next_due || "—"}{r.overdue_count ? " (overdue)" : ""}</b></span>
                      <span>Visits completed: <b className="text-slate-700">{r.visits_completed}/{r.visits_total}</b></span>
                    </div>
                  </div>
                  <button onClick={() => markComplete(r)} data-testid={`wc-complete-${i}`} disabled={!r.next_appt_id}
                    className="shrink-0 flex items-center gap-1.5 bg-[#12355B] hover:bg-[#0d2847] text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-40">
                    <CheckCircle2 className="w-4 h-4" /> Mark Visit Complete
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
export default WarrantyCompliance;
